/**
 * Turning "this row changed" into "do this to the window".
 *
 * `rowLifecycle` answers whether a row belongs in a view and where; this turns
 * that answer into operations on a dense buffer that holds only a slice of the
 * view. The two are separate because the first is true of every view type and
 * the second is true only of a window -- a board's stacks and a gallery's cards
 * take the same decision and do something else with it.
 *
 * Pure, and the reason it is worth being pure is the index arithmetic. A
 * position is relative to the rows being compared, which is the window; the
 * buffer wants an absolute index into the view. Getting that wrong puts a row
 * one place out and nothing tells you until somebody scrolls.
 */
import type { RowData } from '@/api/types'
import type { RowAction, RowPosition } from './rowLifecycle'
import { indexOfRow, type RowWindow } from './rowWindow'

export type WindowOperation =
  | { type: 'insert'; row: RowData; index: number }
  | { type: 'update'; row: RowData }
  | { type: 'remove'; rowId: number }
  | { type: 'move'; rowId: number; toIndex: number }

/**
 * Where a row belongs in the whole view, given where it belongs among the rows
 * the window is holding.
 *
 * One special case, and it is worth being clear about why there is only one.
 * A row that sorts before everything held, with rows above the window, does not
 * belong at the top of the *view* -- it belongs somewhere in the part nobody has
 * fetched. `start + sortedIndex` would answer `start`, which the buffer reads as
 * "inside, at the front" and would hold a row it has no business holding. An
 * index above the window shifts the window down and holds nothing, which is
 * right.
 *
 * There is deliberately no mirror at the other end, and it is a choice rather
 * than an oversight. For an *insert* the arithmetic already gives the right
 * answer: a row sorting after everything held works out to `range.end`, which
 * the buffer reads as "outside, below" while rows exist below it, and as an
 * append when the window reaches the bottom. For a *replacement* the two differ,
 * because the row was taken out of the comparison set before its position was
 * computed. Without a branch it lands at the last held index and stays on
 * screen; with one it leaves the window entirely.
 *
 * Keeping it is the better of the two. Neither placement is knowable -- the row
 * sorts after everything held, and there are rows below nobody has fetched, so
 * it could belong anywhere among them -- and a row that vanishes from under
 * somebody the moment another person edits it is the worse way to be wrong about
 * that. A scroll refetch settles it either way.
 */
export function indexForPosition(window: RowWindow, position: RowPosition): number {
  if (position.isFirst && window.range.start > 0) return window.range.start - 1
  return window.range.start + position.sortedIndex
}

/**
 * What a window should do about a row that changed.
 *
 * A replacement is up to two operations: the values always, and a move when the
 * row no longer sits where it belongs. They are separate because the update is
 * what the reader sees immediately and the move is what keeps the view honest;
 * a single "replace at index" would have to choose between them.
 *
 * The move is decided by comparing where the row *is* with where the position
 * says it goes, and deliberately not by `flags.matchSortings`. That flag is
 * answered against a comparison set the row has been taken out of, so it reads
 * "correctly sorted" for every replacement -- true, and useless here. It means
 * something for the tab that made the edit, where the row is still in the set
 * and the answer is what decides whether to badge it rather than move it.
 */
export function operationsFor(window: RowWindow, action: RowAction): WindowOperation[] {
  switch (action.type) {
    case 'none':
      return []

    case 'remove':
      return [{ type: 'remove', rowId: action.rowId }]

    case 'insert':
      // A row the window is already holding is not inserted again. It can be
      // one: this tab creates a row the filters exclude and keeps it on screen,
      // badged, until the selection leaves -- so an edit that makes it match is
      // an `insert` of a row that never left. Inserting would put a second copy
      // of it in the buffer and count it twice.
      return indexOfRow(window, action.row.id) === -1
        ? [
            {
              type: 'insert',
              row: action.row,
              index: indexForPosition(window, action.position),
            },
          ]
        : replaceInPlace(window, action.row.id, action.row, action.position)

    case 'replace':
      return replaceInPlace(window, action.rowId, action.row, action.position)
  }
}

/**
 * The values, and a move when the row no longer sits where it belongs.
 *
 * Shared by a replacement and by an insert of a row the window already holds,
 * because those are the same thing done for different reasons -- and the
 * position is computed the same way for both, against a comparison set the row
 * has been taken out of.
 */
function replaceInPlace(
  window: RowWindow,
  rowId: number,
  row: RowData,
  position: RowPosition,
): WindowOperation[] {
  const operations: WindowOperation[] = [{ type: 'update', row }]
  const target = indexForPosition(window, position)
  const current = indexOfRow(window, rowId)
  // A row the window does not hold has nothing to move. It can be replaced
  // all the same: the count and the range are unchanged either way.
  if (current !== -1 && current !== target) {
    operations.push({ type: 'move', rowId, toIndex: target })
  }
  return operations
}
